/**
 * Snapping — `docs/property-maps-v2.md` §5.3, §9.3.
 *
 * Within 8 screen pixels, a point placed near an existing **vertex** lands on
 * it, and a point near an **edge** lands on the nearest spot along it. That
 * order matters: a corner is a stronger intention than a side, so a vertex
 * always wins a tie.
 *
 * **Only shapes on visible layers count** (§5.3). Snapping to something the
 * eye has hidden is snapping to something that is not on the screen, which
 * reads as the cursor jumping for no reason.
 *
 * **`⇧` turns it off**, because the one time somebody needs a point *just*
 * beside a corner is the time snapping will not let them have it. It is the
 * same modifier that constrains an angle, which is not a conflict: a
 * constrained edge that then snaps somewhere else is not constrained.
 *
 * In screen space throughout. Eight pixels is eight pixels at every zoom,
 * which is what "within 8 px" means; doing it in metres would snap across
 * half a car park when zoomed out.
 */
import type { Geometry, LngLat } from '@rowted/shared'
import { pointsOf } from '@rowted/shared'
import type { DrawEvent, MapFrame, ScreenPoint } from './types.js'

export const SNAP_PX = 8

export interface SnapTarget {
  /** Already filtered to the visible layers by the caller. */
  geometry: Geometry
}

/**
 * The point to use, after snapping.
 *
 * Curried so a tool can be built once with a snapper that reads whatever is
 * on the map at the moment of the click — the shapes change while a tool is
 * in hand, and a snapshot taken when the tool was chosen would go stale.
 */
export function snapper(
  frame: MapFrame, targets: () => SnapTarget[], pixels = SNAP_PX,
): (point: ScreenPoint, event: DrawEvent) => ScreenPoint {
  return (point, event) => {
    if (event.shift) return point

    let best: { at: ScreenPoint; away: number; vertex: boolean } | null = null

    for (const target of targets()) {
      const screen = pointsOf(target.geometry).map((world) => frame.toPoint(world))

      for (const corner of screen) {
        const away = Math.hypot(corner.x - point.x, corner.y - point.y)
        if (away <= pixels && (!best || best.away > away || (!best.vertex && away <= best.away))) {
          best = { at: corner, away, vertex: true }
        }
      }

      /* Edges only if no vertex has claimed it: a corner beats a side. */
      if (best?.vertex) continue
      for (let i = 1; i < screen.length; i += 1) {
        const at = nearestOnSegment(point, screen[i - 1]!, screen[i]!)
        const away = Math.hypot(at.x - point.x, at.y - point.y)
        if (away <= pixels && (!best || away < best.away)) best = { at, away, vertex: false }
      }
    }

    return best?.at ?? point
  }
}

/** The closest point on a segment, clamped to its ends. */
export function nearestOnSegment(
  point: ScreenPoint, a: ScreenPoint, b: ScreenPoint,
): ScreenPoint {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSquared = dx * dx + dy * dy
  if (lengthSquared === 0) return a
  const along = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared))
  return { x: a.x + along * dx, y: a.y + along * dy }
}

/** Every world coordinate on a set of shapes, for a hit test. */
export const verticesOf = (geometry: Geometry): LngLat[] => pointsOf(geometry)
